import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import Button from 'components/form/Button.component';
import CurrencySign from 'components/price/CurrencySign.component';
import { DOWNLOAD_EXCEL } from 'constants/csv.const';
import { useTheme } from 'providers/Theme.provider';
import { selectCurrentSalonCurrency } from 'selectors/currentSalon.selectors';
import asArray from 'utils/arrays/asArray.util';
import { exportCSV } from 'utils/converters/csv.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import currencySign from 'utils/intl/currencySign.util';
import createNodeKey from 'utils/react/createNodeKey.util';
import truncateString from 'utils/strings/truncateString.util';
import C from 'utils/values/constant.util';
import fixed from 'utils/values/fixed.util';

import { convertToMoneyAfterVat } from 'reports/utils';

import CN from './BookingValByServiceTable.module.scss';

const TABLE_HEADERS = C(['Service', 'Booking Value', 'Count']);

const BookingValByServiceTable = ({ appointmentsReport, startDate, endDate, currentSign }) => {
  const { isDarkMode } = useTheme();

  const response = appointmentsReport?.lastRequestResponse?.service_group;

  const compareServicesByRevenue = (e1, e2) => (e1.total <= e2.total ? 1 : -1);

  const tableData = Object.values(response || {}).sort(compareServicesByRevenue);

  const html = tableData.map((value, index) => (
    <tr key={createNodeKey({ index, tag: value.name })}>
      <td className={CN.firstCell}>{truncateString({ value: value.name, max: 24, suffix: '...' })}</td>
      <td>
        <CurrencySign />
        {fixed(convertToMoneyAfterVat(value.total))}
      </td>
      <td>{value.count}</td>
      <td className={CN.lastCell}></td>
    </tr>
  ));

  const handleExportCsv = useCallback(() => {
    const csvData = asArray(tableData).map((row) => {
      const values = [
        row.name,
        currencySign({ value: fixed(convertToMoneyAfterVat(row.total)), currentSalonAbbr: currentSign }),
        row.count,
      ];
      return TABLE_HEADERS.reduce((acc, header, index) => {
        acc[header] = values[index];
        return acc;
      }, {});
    });

    exportCSV({ data: csvData, fileName: 'Booking Value by Service' });
  }, [currentSign, tableData]);

  const hasTableData = fromAnyToBool(tableData.length);

  return (
    <div
      data-bem="BookingValByServiceTable"
      className={cn({
        [CN.component]: true,
        [CN.darkMode]: isDarkMode,
      })}
    >
      <div className={CN.panelHeading}>
        <div className={CN.title}>Booking Value by Service </div>
        <Button onClick={handleExportCsv} disabled={!hasTableData} variant="primaryForce" width="small" height="xs">
          <span className={CN.buttonFont}>{DOWNLOAD_EXCEL}</span>
        </Button>
      </div>

      <div className={CN.timeContainer}>
        <div>
          From <span className={CN.bold}>{startDate}</span>
        </div>
        <div>
          <span className={CN.gap}>to</span> <span className={CN.bold}>{endDate}</span>
        </div>
      </div>
      <div className={CN.scrollContainer}>
        <table className={CN.table} id="BookingValByServiceTable">
          <thead>
            <tr>
              <th className={CN.firstCell}>{TABLE_HEADERS[0]}</th>
              <th>{TABLE_HEADERS[1]}</th>
              <th>{TABLE_HEADERS[2]}</th>
              <th className={CN.lastCell}></th>
            </tr>
          </thead>
          <tbody>{html}</tbody>
        </table>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  appointmentsReport: state?.reports?.appointmentsReport,
  currentSign: selectCurrentSalonCurrency(state),
});

export default connect(mapStateToProps)(BookingValByServiceTable);
